import { motion } from "motion/react";
import { AlertTriangle, Unlink, Ghost, Activity } from "lucide-react";

export function ProblemSection() {
  const problems = [
    {
      icon: Unlink,
      title: "A Fragmentação",
      description: "Seus dados de formulário ficam num plugin, os dados de carrinho no WooCommerce, e as visitas no GA4. Impossível conectar os pontos.",
      color: "#FF4D00",
    },
    {
      icon: Ghost,
      title: "O Buraco Negro",
      description: "Quando um visitante sai sem comprar, ele se torna um fantasma. Você não tem email, não tem nome, não tem como resgatar.",
      color: "#00F5FF",
    },
    {
      icon: Activity,
      title: "A Cegueira de Bot",
      description: "Você paga por tráfego, mas 30%+ pode ser bots distorcendo suas métricas e inflando seus custos.",
      color: "#10B981",
    },
  ];

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,77,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,77,0,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <AlertTriangle className="w-8 h-8 text-[#FF4D00]" />
              <span className="font-['Roboto_Mono'] text-[#FF4D00] text-sm tracking-[0.3em] uppercase">
                A Dura Realidade
              </span>
            </div>
            <h2 className="font-['Sora'] text-4xl md:text-5xl font-bold text-white mb-6">
              Por que o Google Analytics e os{" "}
              <span className="text-[#FF4D00] drop-shadow-[0_0_20px_rgba(255,77,0,0.5)]">
                Pixels Convencionais
              </span>
              <br />
              Estão Falhando com Você?
            </h2>
            <p className="font-['Inter'] text-xl text-gray-300 max-w-3xl mx-auto">
              Você confia em ferramentas que tratam seus visitantes mais valiosos como meros{" "}
              <span className="text-[#FF4D00] font-semibold">"usuários anônimos"</span>.
            </p>
          </motion.div>

          {/* Problems Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ scale: 1.05, y: -10 }}
                className="relative group"
              >
                <div
                  className="bg-[#0B0E14]/80 backdrop-blur-xl border rounded-2xl p-8 h-full transition-all duration-300"
                  style={{
                    borderColor: `${problem.color}40`,
                    boxShadow: `0 0 30px ${problem.color}10, inset 0 0 50px ${problem.color}05`,
                  }}
                >
                  {/* Icon */}
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110"
                    style={{
                      backgroundColor: `${problem.color}20`,
                      boxShadow: `0 0 20px ${problem.color}30`,
                    }}
                  >
                    <problem.icon
                      className="w-8 h-8"
                      style={{ color: problem.color }}
                    />
                  </div>

                  {/* Title */}
                  <h3
                    className="font-['Sora'] text-2xl font-bold mb-4"
                    style={{ color: problem.color }}
                  >
                    {problem.title}
                  </h3>

                  {/* Description */}
                  <p className="font-['Inter'] text-gray-300 leading-relaxed">
                    {problem.description}
                  </p>

                  {/* Glow Effect */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      background: `radial-gradient(circle at center, ${problem.color}15, transparent 70%)`,
                    }}
                  />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Comparison Visual */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-[#0B0E14]/60 backdrop-blur-xl border border-[#FF4D00]/20 rounded-3xl p-12"
            style={{
              boxShadow: "0 0 60px rgba(255,77,0,0.1), inset 0 0 80px rgba(255,77,0,0.05)",
            }}
          >
            <div className="grid md:grid-cols-2 gap-12">
              {/* BEFORE - Chaos */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 bg-[#FF4D00] rounded-full animate-pulse" />
                  <h3 className="font-['Roboto_Mono'] text-[#FF4D00] text-lg tracking-wider">
                    SEM WP TOTAL TRACKER
                  </h3>
                </div>
                <div className="space-y-4">
                  {["GA4", "Pixel Meta", "WooCommerce", "Plugin Formulário", "???"].map((tool, i) => (
                    <motion.div
                      key={i}
                      initial={{ x: -20, opacity: 0 }}
                      whileInView={{ x: 0, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="bg-[#FF4D00]/10 border border-[#FF4D00]/30 rounded-lg p-4 relative"
                      style={{
                        transform: `rotate(${Math.random() * 4 - 2}deg)`,
                      }}
                    >
                      <span className="font-['Roboto_Mono'] text-gray-400 text-sm">
                        {tool}
                      </span>
                      {i < 4 && (
                        <div className="absolute -bottom-2 left-1/2 w-px h-4 bg-[#FF4D00]/40" />
                      )}
                    </motion.div>
                  ))}
                </div>
                <p className="font-['Inter'] text-gray-400 text-center mt-6 italic">
                  Caos. Fragmentação. Dados Isolados.
                </p>
              </div>

              {/* AFTER - Order */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 bg-[#10B981] rounded-full animate-pulse" />
                  <h3 className="font-['Roboto_Mono'] text-[#10B981] text-lg tracking-wider">
                    COM WP TOTAL TRACKER
                  </h3>
                </div>
                <div className="relative">
                  {/* Central Hub */}
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ type: "spring", duration: 0.8 }}
                    className="bg-gradient-to-br from-[#00F5FF]/20 to-[#10B981]/20 border-2 border-[#00F5FF] rounded-2xl p-8 text-center relative"
                    style={{
                      boxShadow: "0 0 40px rgba(0,245,255,0.3), inset 0 0 60px rgba(0,245,255,0.1)",
                    }}
                  >
                    <h4 className="font-['Sora'] text-2xl font-bold text-[#00F5FF] mb-2">
                      WP Total Tracker
                    </h4>
                    <p className="font-['Roboto_Mono'] text-xs text-gray-400">
                      Hub Central de Inteligência
                    </p>

                    {/* Connection Lines */}
                    {[0, 1, 2, 3].map((i) => (
                      <motion.div
                        key={i}
                        className="absolute w-px h-12 bg-gradient-to-b from-[#00F5FF] to-transparent"
                        style={{
                          top: i < 2 ? "-48px" : "100%",
                          left: i % 2 === 0 ? "25%" : "75%",
                        }}
                        initial={{ scaleY: 0 }}
                        whileInView={{ scaleY: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.5 + i * 0.1 }}
                      />
                    ))}
                  </motion.div>

                  {/* Connected Data Points */}
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    {["Visitas", "Formulários", "Carrinhos", "Vendas"].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.8 + i * 0.1 }}
                        className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg p-3 text-center"
                      >
                        <span className="font-['Roboto_Mono'] text-xs text-gray-400">
                          {item}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>
                <p className="font-['Inter'] text-gray-300 text-center mt-6 font-semibold">
                  Ordem. Conexão. Dados Unificados.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Closing Statement */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mt-16"
          >
            <p className="font-['Sora'] text-2xl text-white mb-4">
              É hora de parar de voar às cegas.
            </p>
            <p className="font-['Sora'] text-3xl font-bold text-[#00F5FF] drop-shadow-[0_0_30px_rgba(0,245,255,0.5)]">
              É hora de arquitetura de dados.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
